import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Button from '../../components/reusable/button/button.component';
import FulfillmentsTable from '../../components/reusable/tables/fulfillments-table/fulfillments-table.component';
import Pagination from '../../components/reusable/pagination/pagination.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import TableControls from '../../components/reusable/table-controls/table-controls.component';

import useTableState from '../../hooks/useTableState';
import Client from '../../tools/client';

import {
  ContentContainer,
  MainContainer,
  MainTitle
} from '../../styles/page.styles';

const client = new Client();

const FulfillmentsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fulfillments, setFulfillments] = useState([]);
  const [count, setCount] = useState(null);

  const {
    page,
    size,
    search,
    sortKey,
    sortDirection,
    filters,
    setPage,
    setSize,
    setSearch,
    setSortKey,
    setSortDirection,
    setFilters
  } = useTableState({ defaultSize: 25 });

  const isInitialMount = useRef(true);

  const getFulfillments = useCallback(async () => {
    setLoading(true);

    let query = `?page=${page}&size=${size}`;
    if (search) query += `&search=${encodeURIComponent(search)}`;
    if (sortDirection) query += `&sortDirection=${sortDirection}`;
    if (sortKey) query += `&sortKey=${sortKey}`;

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        query += `&filter[${key}]=${value.join(',')}`;
      } else if (value) {
        query += `&filter[${key}]=${encodeURIComponent(value)}`;
      }
    });

    try {
      const res = await client.getAllFulfillments(query);
      setFulfillments(res);
      setCount(res.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, size, search, sortKey, sortDirection, filters]);

  useEffect(() => {
    getFulfillments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    getFulfillments();
  }, [page, size, search, sortKey, sortDirection, filters, getFulfillments]);

  const handleSearchSubmit = useCallback(() => {}, []);

  // Export configuration
  const exportFn = async (currentFilters) => {
    let query = '?';
    if (sortDirection) query += `sortDirection=${sortDirection}&`;
    if (sortKey) query += `sortKey=${sortKey}&`;

    Object.entries(currentFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        query += `filter[${key}]=${value.join(',')}&`;
      } else if (value) {
        query += `filter[${key}]=${value}&`;
      }
    });

    const res = await client.exportFulfillments(query);
    return res.rows || [];
  };

  const formatExportData = (data) => {
    return data.map((fulfillment) => {
      const createdDate = dayjs(fulfillment.createdAt).format('MM/DD/YYYY');
      const shippedDate = fulfillment.shippedAt ? dayjs(fulfillment.shippedAt).format('MM/DD/YYYY') : '';
      const deliveredDate = fulfillment.deliveredAt ? dayjs(fulfillment.deliveredAt).format('MM/DD/YYYY') : '';

      return [
        fulfillment.id || '',
        fulfillment.status || '',
        fulfillment.trackingNumber || '',
        fulfillment.Order?.refId || '',
        createdDate,
        shippedDate,
        deliveredDate
      ];
    });
  };

  const exportHeaders = ['ID', 'Status', 'Tracking Number', 'Order Ref ID', 'Created Date', 'Shipped Date', 'Delivered Date'];

  const filterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'checkbox',
      options: ['pending', 'shipped', 'delivered']
    }
  ];

  return (
    <MainContainer>
      <ContentContainer>
        <MainTitle>Fulfillments</MainTitle>

        <TableControls
          search={search}
          setSearch={setSearch}
          onSearchSubmit={handleSearchSubmit}
          size={size}
          setSize={setSize}
          filters={filters}
          setFilters={setFilters}
          filterConfig={filterConfig}
          entityName="fulfillments"
          exportFn={exportFn}
          formatExportData={formatExportData}
          exportHeaders={exportHeaders}
          additionalButtons={
            <Button onClick={() => navigate('/fulfillments/ready')}>
              Ready to Fulfill
            </Button>
          }
        />

        {loading ? (
          <Spinner />
        ) : (
          <>
            <FulfillmentsTable
              fulfillments={fulfillments}
              sortKey={sortKey}
              setSortKey={setSortKey}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              navigateToFulfillment={(id) => navigate(`/fulfillments/${id}`)}
            />

            <Pagination
              count={count}
              size={size}
              page={page}
              changePage={setPage}
            />
          </>
        )}
      </ContentContainer>
    </MainContainer>
  );
};

export default FulfillmentsPage;
